import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Home, Calendar, User } from "lucide-react";
import { formatDate } from "./utils";

interface ClosingDate {
  _id: string;
  matterId?: { _id: string; title: string; matterNumber?: string };
  clientId?: { displayName: string };
  transactionType: string;
  property?: { address?: { street?: string; city?: string } };
  dates: { closingDate: string };
}

interface ClosingDatesWidgetProps {
  closingDates: ClosingDate[];
}

const transactionTypeLabels: Record<string, string> = {
  purchase: "Purchase",
  sale: "Sale",
  mortgage: "Mortgage",
  refinance: "Refinance",
  private_lending: "Private Lending",
};

const ClosingDatesWidget: React.FC<ClosingDatesWidgetProps> = ({ closingDates }) => {
  const navigate = useNavigate();

  // Compute how many days until closing
  const getDaysUntil = (dateStr: string): number => {
    const closing = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.ceil((closing.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getUrgencyStyle = (days: number) => {
    if (days <= 3) return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" };
    if (days <= 7) return { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" };
    if (days <= 14) return { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" };
    return { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200" };
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
    >
      <div
        className="px-6 py-4"
        style={{ backgroundColor: "var(--color-primary)" }}
      >
        <div className="flex items-center gap-3">
          <Home className="h-6 w-6 text-emerald-300" aria-hidden="true" />
          <h2 className="text-xl font-bold text-white">
            Upcoming Closings
          </h2>
        </div>
      </div>

      <div className="p-6">
        {closingDates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "var(--color-primary-light)" }}
            >
              <Home className="h-8 w-8" style={{ color: "var(--color-accent)" }} />
            </div>
            <p className="font-semibold text-slate-700 mb-1">No upcoming closings</p>
            <p className="text-sm text-slate-400">
              Real estate closings will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {closingDates.map((cd, idx) => {
              const daysUntil = getDaysUntil(cd.dates.closingDate);
              const urgency = getUrgencyStyle(daysUntil);
              const address = cd.property?.address;
              const addressStr = [address?.street, address?.city]
                .filter(Boolean)
                .join(", ");

              return (
                <motion.div
                  key={cd._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  className={`${urgency.bg} border ${urgency.border} rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => {
                    if (cd.matterId?._id) {
                      navigate(`/matters/${cd.matterId._id}`);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <h3 className="text-sm font-semibold text-slate-800 truncate">
                        {cd.matterId?.title || "Untitled Matter"}
                      </h3>

                      {cd.clientId?.displayName && (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
                          <span className="text-xs font-medium" style={{ color: "var(--color-accent)" }}>
                            {cd.clientId.displayName}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                        {addressStr && (
                          <span className="flex items-center gap-1">
                            <Home className="h-3 w-3" aria-hidden="true" />
                            {addressStr}
                          </span>
                        )}
                        <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-medium">
                          {transactionTypeLabels[cd.transactionType] || cd.transactionType}
                        </span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-slate-500" />
                        <span className="text-sm font-semibold text-slate-700">
                          {formatDate(cd.dates.closingDate)}
                        </span>
                      </div>
                      <span className={`text-xs font-bold ${urgency.text} mt-1 block`}>
                        {daysUntil === 0
                          ? "Today"
                          : daysUntil === 1
                          ? "Tomorrow"
                          : `${daysUntil} days`}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default ClosingDatesWidget;
